/**
 * Garage Metafield Sync
 * Syncs localStorage vehicles to Shopify Cart Attributes
 *
 * IMPORTANT: This creates a bridge via cart attributes.
 * To complete the sync to Customer Metafields, you MUST set up Shopify Flow:
 *
 * Shopify Flow Configuration:
 * ---------------------------
 * Trigger: "Order Created" or use a Flow-compatible app for "Cart Updated"
 * Condition: Check if cart attribute 'garage_vehicles' exists
 * Action: "Update Customer Metafield"
 *   - Namespace: garage
 *   - Key: vehicles
 *   - Value: {{ customer.cart_level_attribute.garage_vehicles }}
 *   - Type: JSON
 *
 * Without this Flow, vehicles will only persist in the Cart, not on the Customer profile.
 */
(function () {
	"use strict";

	const STORAGE_KEY = "searchTerms";
	const SYNC_FLAG = "partify_garage_last_sync";
	const SYNC_INTERVAL = 30000; // 30 seconds

	/**
	 * Initialize garage sync
	 */
	function init() {
		// Load garage from metafields if customer is logged in
		loadGarageFromMetafields();

		// Set up periodic sync
		syncGarageToCart();
		setInterval(syncGarageToCart, SYNC_INTERVAL);

		// Sync on page visibility change (user returns to tab)
		document.addEventListener("visibilitychange", () => {
			if (document.visibilityState === "visible") {
				syncGarageToCart();
			}
		});

		// Sync before unload
		window.addEventListener("beforeunload", () => {
			syncGarageToCartSync(); // Synchronous version
		});

		// Listen for custom garage update events
		window.addEventListener("garageUpdated", syncGarageToCart);
	}

	/**
	 * Load garage from customer metafields and merge with localStorage
	 * Server metafield data takes priority
	 */
	function loadGarageFromMetafields() {
		const customerData = window.__PARTIFY_CUSTOMER__;

		if (!customerData || !customerData.garage) {
			console.log("[GarageSync] No customer metafield data");
			return;
		}

		try {
			let serverVehicles = [];

			// Handle both string and object metafield values
			if (typeof customerData.garage === "string") {
				serverVehicles = JSON.parse(customerData.garage);
			} else if (Array.isArray(customerData.garage)) {
				serverVehicles = customerData.garage;
			}

			if (!Array.isArray(serverVehicles) || serverVehicles.length === 0) {
				console.log("[GarageSync] No vehicles in metafields");
				return;
			}

			// Get local vehicles
			let localVehicles = [];
			try {
				localVehicles = JSON.parse(localStorage.getItem(STORAGE_KEY)) || [];
			} catch (e) {
				localVehicles = [];
			}

			// Merge: Server takes priority, but add local-only vehicles
			const merged = mergeVehicles(serverVehicles, localVehicles);

			// Update localStorage with merged data
			localStorage.setItem(STORAGE_KEY, JSON.stringify(merged));

			// Trigger UI refresh
			window.dispatchEvent(
				new CustomEvent("garageUpdated", { detail: merged }),
			);

			console.log(
				"[GarageSync] Loaded",
				serverVehicles.length,
				"vehicles from metafields",
			);
		} catch (error) {
			console.error("[GarageSync] Error loading from metafields:", error);
		}
	}

	/**
	 * Merge vehicle arrays, deduplicating by YMM
	 * Server vehicles take priority
	 */
	function mergeVehicles(serverVehicles, localVehicles) {
		const map = new Map();

		// Server vehicles first (higher priority)
		serverVehicles.forEach((v) => {
			const key = normalizeYMM(v.ymm);
			if (key) map.set(key, v);
		});

		// Add local vehicles that aren't on server
		localVehicles.forEach((v) => {
			const key = normalizeYMM(v.ymm);
			if (key && !map.has(key)) {
				map.set(key, v);
			}
		});

		return Array.from(map.values());
	}

	/**
	 * Normalize YMM for comparison
	 */
	function normalizeYMM(ymm) {
		if (!ymm) return "";
		return ymm.toLowerCase().trim().replace(/\s+/g, " ");
	}

	/**
	 * Sync garage data to cart attributes (async)
	 */
	async function syncGarageToCart() {
		try {
			const vehicles = JSON.parse(localStorage.getItem(STORAGE_KEY)) || [];
			const syncData = JSON.stringify(vehicles);

			// Check if data has changed since last sync
			const lastSync = sessionStorage.getItem(SYNC_FLAG);
			if (lastSync === syncData) {
				return; // No changes, skip sync
			}

			const response = await fetch("/cart/update.js", {
				method: "POST",
				headers: { "Content-Type": "application/json" },
				body: JSON.stringify({
					attributes: {
						garage_vehicles: syncData,
						garage_sync_timestamp: new Date().toISOString(),
						garage_vehicle_count: String(vehicles.length),
					},
				}),
			});

			if (response.ok) {
				sessionStorage.setItem(SYNC_FLAG, syncData);
				console.log("[GarageSync] Synced", vehicles.length, "vehicles to cart");
			}
		} catch (error) {
			console.error("[GarageSync] Sync error:", error);
		}
	}

	/**
	 * Sync garage data to cart attributes (synchronous, for beforeunload)
	 */
	function syncGarageToCartSync() {
		try {
			const vehicles = JSON.parse(localStorage.getItem(STORAGE_KEY)) || [];

			// Use sendBeacon for synchronous send during page unload
			const data = JSON.stringify({
				attributes: {
					garage_vehicles: JSON.stringify(vehicles),
					garage_sync_timestamp: new Date().toISOString(),
				},
			});

			navigator.sendBeacon(
				"/cart/update.js",
				new Blob([data], { type: "application/json" }),
			);
		} catch (error) {
			// Silent fail during unload
		}
	}

	// Initialize when DOM is ready
	if (document.readyState === "loading") {
		document.addEventListener("DOMContentLoaded", init);
	} else {
		init();
	}
})();
